import type { QuotationInvoiceCustomerListItem } from "@/modules/quotation-invoice/domain/types";
import {
    QUOTATION_INVOICE_CUSTOMER_FILTER_ALL,
    QUOTATION_INVOICE_CUSTOMER_UNCATEGORIZED,
} from "@/modules/quotation-invoice/domain/types";
import { useDisclosure } from "@/shared/hooks/useDisclosure";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Button, Input, Modal, confirm, useDebounceCallback } from "@devhop/ui";
import { FolderTreeIcon, PencilIcon, PlusIcon, SearchIcon, TrashIcon, UserCheckIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type {
    CreateCustomerInput,
    UpdateCustomerInput,
} from "../api/client";
import {
    useCreateQuotationInvoiceCustomer,
    useQuotationInvoiceCustomerCategoriesQuery,
    useQuotationInvoiceCustomersQuery,
    useUpdateQuotationInvoiceCustomer,
} from "../api/queries";
import { CustomerCategoryManagementModal } from "./CustomerCategoryManagementModal";
import { CustomerForm } from "./CustomerForm";

export function CustomerManagementModal({
  open,
  onOpenChange,
  onRefresh,
  onSelectCustomer,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRefresh?: () => void;
  /** When provided, show "ໃຊ້ລູກຄ້ານີ້" and call with customer id when clicked */
  onSelectCustomer?: (customerId: string) => void;
}) {
  const createModal = useDisclosure();
  const editModal = useDisclosure<QuotationInvoiceCustomerListItem>();
  const categoryManageModal = useDisclosure();
  const [listCategoryFilter, setListCategoryFilter] = useState(
    QUOTATION_INVOICE_CUSTOMER_FILTER_ALL,
  );
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSetSearch = useDebounceCallback((value: string) => {
    setSearchQuery(value);
  }, 300);

  useEffect(() => {
    if (!open) {
      setSearchInput("");
      setSearchQuery("");
    }
  }, [open]);

  const { data: categoriesData } = useQuotationInvoiceCustomerCategoriesQuery(
    {},
  );

  const filterSelectOptions = useMemo(() => {
    const items = categoriesData?.items ?? [];
    const activeSorted = [...items]
      .filter((c) => c.isActive)
      .sort(
        (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
      );
    return [
      { value: QUOTATION_INVOICE_CUSTOMER_FILTER_ALL, label: "ທັງໝົດ" },
      {
        value: QUOTATION_INVOICE_CUSTOMER_UNCATEGORIZED,
        label: "ບໍ່ມີຫມວດຫມູ່",
      },
      ...activeSorted.map((c) => ({ value: c.id, label: c.name })),
    ];
  }, [categoriesData?.items]);

  const activeCategorySelectOptions = useMemo(() => {
    const items = categoriesData?.items ?? [];
    return [...items]
      .filter((c) => c.isActive)
      .sort(
        (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
      )
      .map((c) => ({ value: c.id, label: c.name }));
  }, [categoriesData?.items]);

  const editCategoryOptions = useMemo(() => {
    const cur = editModal.data;
    if (!cur?.categoryId || !cur.categoryName) return activeCategorySelectOptions;
    if (activeCategorySelectOptions.some((o) => o.value === cur.categoryId)) {
      return activeCategorySelectOptions;
    }
    return [
      ...activeCategorySelectOptions,
      {
        value: cur.categoryId,
        label: `${cur.categoryName} (ບໍ່ມີໃນລາຍການ)`,
      },
    ];
  }, [activeCategorySelectOptions, editModal.data]);

  const { data: customersData, refetch } = useQuotationInvoiceCustomersQuery({
    limit: 200,
    includeInactive: true,
    q: searchQuery.trim() || undefined,
    ...(listCategoryFilter !== QUOTATION_INVOICE_CUSTOMER_FILTER_ALL
      ? { categoryId: listCategoryFilter }
      : {}),
  });
  const customers = customersData?.items ?? [];

  const createCustomer = useCreateQuotationInvoiceCustomer();
  const updateCustomer = useUpdateQuotationInvoiceCustomer();

  const refresh = () => {
    refetch();
    onRefresh?.();
  };

  const handleCreate = async (
    vals: CreateCustomerInput | UpdateCustomerInput,
  ) => {
    const name = vals.name?.trim();
    if (!name) return;
    const input: CreateCustomerInput = {
      name,
      address: vals.address ?? null,
      contact: vals.contact ?? null,
      taxId: vals.taxId ?? null,
      categoryId: vals.categoryId ?? null,
    };
    const created = await createCustomer.mutateAsync(input);
    createModal.close();
    refresh();
    if (onSelectCustomer && created.id) {
      onSelectCustomer(created.id);
      onOpenChange(false);
    }
  };

  const handleUpdate = async (
    vals: {
      name?: string;
      address?: string | null;
      contact?: string | null;
      taxId?: string | null;
      categoryId?: string | null;
      isActive?: boolean;
    },
  ) => {
    if (!editModal.data) return;
    await updateCustomer.mutateAsync({ id: editModal.data.id, ...vals });
    editModal.close();
    refresh();
  };

  const handleDeactivate = async (
    customer: QuotationInvoiceCustomerListItem,
  ) => {
    const ok = await confirm({
      title: "ປິດການໃຊ້ງານລູກຄ້າ",
      description: `ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການປິດການໃຊ້ງານລູກຄ້າ "${customer.name}"? ລູກຄ້ານີ້ຈະບໍ່ສະແດງໃນລາຍການເລືອກເວລາສ້າງເອກະສານ.`,
      actionText: "ປິດການໃຊ້ງານ",
      ActionProps: { variant: "destructive" },
    });
    if (ok) {
      await updateCustomer.mutateAsync({ id: customer.id, isActive: false });
      editModal.close();
      refresh();
    }
  };

  const handleReactivate = async (
    customer: QuotationInvoiceCustomerListItem,
  ) => {
    await updateCustomer.mutateAsync({ id: customer.id, isActive: true });
    editModal.close();
    refresh();
  };

  return (
    <>
      <Modal
        open={open}
        onOpenChange={onOpenChange}
        title="ຈັດການລູກຄ້າ"
        size="lg"
      >
        <div className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-end">
              <div className="relative w-full sm:max-w-xs">
                <SearchIcon className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchInput(value);
                    debouncedSetSearch(value);
                  }}
                  placeholder="ຄົ້ນຫາຊື່, ເບີໂທ ຫຼື ທີ່ຢູ່..."
                  className="pl-9"
                />
              </div>
              <div className="space-y-2 sm:max-w-xs">
                <span className="text-muted-foreground text-xs">
                  ກອງຕາມຫມວດຫມູ່
                </span>
                <SimpleSelect
                  value={listCategoryFilter}
                  onValueChange={setListCategoryFilter}
                  options={filterSelectOptions}
                  className="h-9 w-full"
                />
              </div>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={categoryManageModal.open}
                className="gap-1"
              >
                <FolderTreeIcon className="h-4 w-4" />
                ຫມວດຫມູ່ລູກຄ້າ
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={createModal.open}
              >
                <PlusIcon className="mr-2 h-4 w-4" />
                ເພີ່ມລູກຄ້າ
              </Button>
            </div>
          </div>

          <div className="rounded-lg border">
            <div className="max-h-[calc(100vh-16rem)] divide-y overflow-y-auto">
              {customers.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground text-sm">
                  {searchQuery.trim()
                    ? "ບໍ່ພົບລູກຄ້າຕາມຄຳຄົ້ນຫາ"
                    : "ບໍ່ພົບລູກຄ້າໃນຕົວກອງນີ້"}
                </div>
              ) : (
                customers.map((customer) => (
                  <div
                    key={customer.id}
                    className="flex items-center justify-between gap-2 p-4"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={
                            customer.isActive
                              ? "font-medium"
                              : "font-medium text-muted-foreground line-through"
                          }
                        >
                          {customer.name}
                        </span>
                        {customer.categoryName && (
                          <span className="rounded border bg-muted/50 px-1.5 py-0.5 text-muted-foreground text-xs">
                            {customer.categoryName}
                          </span>
                        )}
                        {!customer.isActive && (
                          <span className="rounded bg-muted px-1.5 py-0.5 text-muted-foreground text-xs">
                            ປິດການໃຊ້ງານ
                          </span>
                        )}
                      </div>
                      {(customer.address || customer.contact) && (
                        <div className="line-clamp-2 text-muted-foreground text-sm">
                          {[customer.address, customer.contact]
                            .filter(Boolean)
                            .join(" • ")}
                        </div>
                      )}
                    </div>
                    <div className="ml-2 flex shrink-0 items-center gap-2">
                      {onSelectCustomer && customer.isActive && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            onSelectCustomer(customer.id);
                            onOpenChange(false);
                          }}
                        >
                          <UserCheckIcon className="mr-1 h-4 w-4" />
                          ໃຊ້ລູກຄ້ານີ້
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => editModal.openWith(customer)}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </Button>
                      {customer.isActive ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeactivate(customer)}
                          className="text-destructive hover:text-destructive"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleReactivate(customer)}
                        >
                          ເປີດໃຊ້ງານ
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </Modal>

      <CustomerCategoryManagementModal
        open={categoryManageModal.isOpen}
        onOpenChange={(v) =>
          v ? categoryManageModal.open() : categoryManageModal.close()
        }
        onRefresh={refresh}
      />

      <Modal
        open={!!createModal.isOpen}
        onOpenChange={createModal.toggle}
        title="ເພີ່ມລູກຄ້າ"
        size="sm"
      >
        <CustomerForm
          categoryOptions={activeCategorySelectOptions}
          onSubmit={handleCreate}
          submitting={createCustomer.isPending}
        />
      </Modal>

      <Modal
        open={!!editModal.isOpen}
        onOpenChange={editModal.toggle}
        title="ແກ້ໄຂລູກຄ້າ"
        size="sm"
      >
        <CustomerForm
          key={editModal.data?.id}
          categoryOptions={editCategoryOptions}
          initialValues={editModal.data ?? undefined}
          onSubmit={handleUpdate}
          submitting={updateCustomer.isPending}
        />
      </Modal>
    </>
  );
}
